import { apiClient } from "@/lib/api/client";
import type { CallSessionResponse, CallTokenResponse } from "@/types/call";

/**
 * The four documented Doctor Web video-call endpoints, and nothing else.
 * They go through the same `apiClient` (and therefore the same `/api/proxy/$`
 * pass-through + Firebase ID token) as every other request in the portal.
 *
 * Contract summary: FRONTEND.MD §9.
 */
export const videoCallService = {
  /** Active session for this booking. 404 = nothing to resume. */
  getCallSession: (bookingId: string, signal?: AbortSignal) =>
    apiClient.get<CallSessionResponse>(`/bookings/${bookingId}/call`, signal),

  /** Creates the CallSession. The backend mints the roomId. */
  initiateCall: (bookingId: string) =>
    apiClient.post<CallSessionResponse>(`/bookings/${bookingId}/call/initiate`),

  /** Short-lived ZegoCloud join credentials for the calling doctor. */
  getCallToken: (bookingId: string) =>
    apiClient.post<CallTokenResponse>(`/bookings/${bookingId}/call/token`),

  /** Ends the active session; the backend computes `duration` (seconds). */
  endCall: (bookingId: string) =>
    apiClient.post<CallSessionResponse>(`/bookings/${bookingId}/call/end`),

  /**
   * Uploads the doctor's recorded audio for AI transcript processing.
   * Backend: POST /api/bookings/:bookingId/ai-session/transcript
   * Expects multipart/form-data with field name "audio".
   * Speaker is derived from auth token; do not send a speaker field.
   * 10MB limit per upload (multer).
   */
  uploadDoctorTranscript: (bookingId: string, audioBlob: Blob) => {
    const formData = new FormData();
    formData.append("audio", audioBlob, "doctor-audio.webm");
    // No `headers` override here — that field skips the default Firebase auth
    // header entirely (see `send()`'s `extraHeaders` branch), and this upload
    // needs the same doctor ID token every other endpoint sends.
    return apiClient.request<{ message: string; segment: unknown }>(
      `/bookings/${bookingId}/ai-session/transcript`,
      {
        method: "POST",
        body: formData,
      },
    );
  },
};
